/**
 * generatePanoDetayV2.js
 * Pano detay çizimini THREE.js sahnesine çizer.
 * pano-detay-spec.md'ye uygun: Topology A/B/C, çok sayaçlı grid, 3 bölme, ölçülendirme
 *
 * Koordinat sistemi: Y yukarı pozitif (THREE.js standardı)
 * Birim: cm (tüm değerler cm cinsinden, Scl çarpanıyla dünya birimine çevrilir)
 */

import * as THREE from 'three';
import {
  calculateTotalPanelSize,
  calculateCompartmentSplit,
  calculateMeterGrid,
} from './panelSizeCalculator';
import {
  generateMeterPositions,
  generateDoorRects,
  getCompartmentLayout,
} from './panelDetailDrawing';
import { getMeterPhysical } from './meterDrawing';
import { getBreakerPhysical } from './breakerDrawing';

const PANO_DETAY_TAG = 'isPanoDetay';

// Ölçek: 1 cm = Scl dünya birimi
const Scl = 2;

// ─── Renkler ────────────────────────────────────────────
const COLORS = {
  frame: 0xcccccc,
  innerFrame: 0x888888,
  meter3Phase: 0x4488ff,
  meter1Phase: 0x44cc88,
  breaker: 0xff8844,
  busbar: 0xddaa22,
  distribution: 0xaa66cc,
  door: 0x555555,
  doorKnob: 0xeeeeee,
  dimension: 0x999999,
  text: '#cccccc',
  titleText: '#ffffff',
  dimText: '#aaaaaa',
};

// ─── Temel Çizim Yardımcıları ───────────────────────────

function createLine(x1, y1, x2, y2, color = COLORS.frame) {
  const points = [
    new THREE.Vector3(x1 * Scl, y1 * Scl, 0),
    new THREE.Vector3(x2 * Scl, y2 * Scl, 0),
  ];
  const geo = new THREE.BufferGeometry().setFromPoints(points);
  const mat = new THREE.LineBasicMaterial({ color });
  const line = new THREE.Line(geo, mat);
  line.userData = { [PANO_DETAY_TAG]: true };
  return line;
}

function createRect(x, y, w, h, color = COLORS.frame) {
  const points = [
    new THREE.Vector3(x * Scl, y * Scl, 0),
    new THREE.Vector3((x + w) * Scl, y * Scl, 0),
    new THREE.Vector3((x + w) * Scl, (y + h) * Scl, 0),
    new THREE.Vector3(x * Scl, (y + h) * Scl, 0),
    new THREE.Vector3(x * Scl, y * Scl, 0),
  ];
  const geo = new THREE.BufferGeometry().setFromPoints(points);
  const mat = new THREE.LineBasicMaterial({ color });
  const line = new THREE.Line(geo, mat);
  line.userData = { [PANO_DETAY_TAG]: true };
  return line;
}

function createFilledRect(x, y, w, h, color, opacity = 0.15) {
  const geo = new THREE.PlaneGeometry(w * Scl, h * Scl);
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set((x + w / 2) * Scl, (y + h / 2) * Scl, -0.01);
  mesh.userData = { [PANO_DETAY_TAG]: true };
  return mesh;
}

function createCircle(x, y, radius, color = COLORS.doorKnob) {
  const geo = new THREE.CircleGeometry(radius * Scl, 16);
  const mat = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x * Scl, y * Scl, 0.02);
  mesh.userData = { [PANO_DETAY_TAG]: true };
  return mesh;
}

function createTextSprite(text, x, y, fontSize = 8, color = COLORS.text) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const pxScale = 4;
  const pxSize = fontSize * pxScale;
  ctx.font = `bold ${pxSize}px Arial`;
  const metrics = ctx.measureText(text);
  const textWidth = metrics.width;

  canvas.width = textWidth + 4 * pxScale;
  canvas.height = pxSize + 4 * pxScale;
  ctx.font = `bold ${pxSize}px Arial`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(spriteMat);
  sprite.position.set(x * Scl, y * Scl, 0.1);

  const worldW = canvas.width / pxScale;
  const worldH = canvas.height / pxScale;
  sprite.scale.set(worldW, worldH, 1);
  sprite.userData = { [PANO_DETAY_TAG]: true, type: 'text', text };
  return sprite;
}

// ─── Ölçülendirme Çizimi ────────────────────────────────

/**
 * Yatay ölçülendirme çizgisi
 * @param {number} dir - Yön: 1 = yukarı (pano üstünde), -1 = aşağı (pano altında)
 */
function drawDimensionH(group, x1, x2, y, offset, label, config, dir = -1) {
  const gap = config.dimensions.gap || 0;
  const arrow = config.dimensions.arrowSize || 3;
  const ly = y + dir * (gap + offset);
  // Extension lines (gap boşluğu bırakarak başla)
  group.add(createLine(x1, y + dir * gap, x1, ly + dir * 2, COLORS.dimension));
  group.add(createLine(x2, y + dir * gap, x2, ly + dir * 2, COLORS.dimension));
  // Dimension line
  group.add(createLine(x1, ly, x2, ly, COLORS.dimension));
  // Arrows (ok boyutu config'den)
  const aL = arrow * 0.5;
  const aW = arrow * 0.17;
  group.add(createLine(x1, ly, x1 + aL, ly + aW, COLORS.dimension));
  group.add(createLine(x1, ly, x1 + aL, ly - aW, COLORS.dimension));
  group.add(createLine(x2, ly, x2 - aL, ly + aW, COLORS.dimension));
  group.add(createLine(x2, ly, x2 - aL, ly - aW, COLORS.dimension));
  // Text
  group.add(createTextSprite(label, (x1 + x2) / 2, ly + dir * 3, config.dimensions.textSize * 0.6, COLORS.dimText));
}

/**
 * Dikey ölçülendirme çizgisi
 * @param {number} dir - Yön: -1 = sola (pano solunda), 1 = sağa (pano sağında)
 */
function drawDimensionV(group, y1, y2, x, offset, label, config, dir = -1) {
  const gap = config.dimensions.gap || 0;
  const arrow = config.dimensions.arrowSize || 3;
  const lx = x + dir * (gap + offset);
  // Extension lines (gap boşluğu bırakarak başla)
  group.add(createLine(x + dir * gap, y1, lx + dir * 2, y1, COLORS.dimension));
  group.add(createLine(x + dir * gap, y2, lx + dir * 2, y2, COLORS.dimension));
  group.add(createLine(lx, y1, lx, y2, COLORS.dimension));
  // Arrows (ok boyutu config'den)
  const aL = arrow * 0.5;
  const aW = arrow * 0.17;
  group.add(createLine(lx, y1, lx + aW, y1 + aL, COLORS.dimension));
  group.add(createLine(lx, y1, lx - aW, y1 + aL, COLORS.dimension));
  group.add(createLine(lx, y2, lx + aW, y2 - aL, COLORS.dimension));
  group.add(createLine(lx, y2, lx - aW, y2 - aL, COLORS.dimension));
  group.add(createTextSprite(label, lx + dir * 4, (y1 + y2) / 2, config.dimensions.textSize * 0.6, COLORS.dimText));
}

// ─── Gerçek Ölçülü Sayaç Çizimi ────────────────────────

/**
 * Gerçek fiziksel ölçülere dayalı sayaç çizimi (Three.js)
 * @param {THREE.Group} group
 * @param {number} mx - Sayaç sol-alt köşe X (cm)
 * @param {number} my - Sayaç alt kenar Y (cm, Three.js: yukarı pozitif)
 * @param {number} phase - 1 veya 3
 */
function drawRealisticMeter(group, mx, my, phase) {
  const d = getMeterPhysical(phase);
  const w = d.width;
  const h = d.height;
  const cx = mx + w / 2;

  // Three.js Y yukarı pozitif — sayaç alt kenarı my, üst kenarı my + h
  // Terminal bloğu altta, gövde ortada, kanca üstte
  const termTop = my + d.terminalAreaH;
  const bodyBottom = termTop;
  const bodyTop = my + h - d.hookAreaH * 0.25;
  const displayBottom = termTop;
  const displayTop = termTop + d.displayAreaH;

  // ── Ana gövde ──
  const bodyH = bodyTop - bodyBottom;
  group.add(createFilledRect(mx, bodyBottom, w, bodyH, 0xe0dcd5, 0.4));
  group.add(createRect(mx, bodyBottom, w, bodyH, 0x888888));

  // ── Ekran çerçevesi (bezel) ──
  const dispMargin = w * 0.1;
  const dispW = w - 2 * dispMargin;
  const dispBH = d.displayAreaH * 0.42;
  const dispBY = displayTop - d.displayAreaH * 0.06 - dispBH;
  group.add(createFilledRect(mx + dispMargin, dispBY, dispW, dispBH, 0x111820, 0.65));
  group.add(createRect(mx + dispMargin, dispBY, dispW, dispBH, 0x444444));

  // 1F için kırmızı çerçeve
  if (phase === 1) {
    group.add(createRect(mx + dispMargin - 0.15, dispBY - 0.15, dispW + 0.3, dispBH + 0.3, 0xcc4444));
  }

  // ── LCD ekran ──
  const lcdPad = dispW * 0.06;
  const lcdW = dispW - 2 * lcdPad;
  const lcdH = dispBH * 0.42;
  const lcdY = dispBY + dispBH * 0.08;
  group.add(createFilledRect(mx + dispMargin + lcdPad, lcdY, lcdW, lcdH, 0x152535, 0.55));
  const lcdBorder = phase === 1 ? 0xcc4444 : 0x3a6580;
  group.add(createRect(mx + dispMargin + lcdPad, lcdY, lcdW, lcdH, lcdBorder));
  group.add(createTextSprite(
    phase === 3 ? '00000000' : '00000.0',
    cx, lcdY + lcdH / 2, 3, '#70b8c8',
  ));

  // ── LED göstergeleri ──
  const ledCount = phase === 3 ? 5 : 3;
  const ledSpacing = dispW / (ledCount + 1);
  const ledY = dispBY + dispBH * 0.7;
  const ledH = dispBH * 0.15;
  for (let i = 0; i < ledCount; i++) {
    const lx = mx + dispMargin + ledSpacing * (i + 1) - 0.12;
    group.add(createFilledRect(lx, ledY, 0.24, ledH, 0x1a3a1a, 0.5));
  }

  // ── kWh yazısı ──
  group.add(createTextSprite('kWh', cx, displayBottom + d.displayAreaH * 0.4, 3.5, '#777777'));

  // ── Mühür dairesi ──
  const sealR = w * 0.06;
  const sealY = displayBottom + d.displayAreaH * 0.32;
  group.add(createCircle(cx, sealY, sealR, 0xcccccc));

  // ── Küçük portlar ──
  const portR = w * 0.03;
  const portY = displayBottom + d.displayAreaH * 0.2;
  group.add(createCircle(cx - w * 0.13, portY, portR, 0x666666));
  group.add(createCircle(cx + w * 0.13, portY, portR, 0x666666));

  // ── Köşe vidaları ──
  const scrInset = w * 0.08;
  const scrR = w * 0.02;
  [
    [mx + scrInset, bodyTop - scrInset * 1.2],
    [mx + w - scrInset, bodyTop - scrInset * 1.2],
    [mx + scrInset, bodyBottom + scrInset * 1.2],
    [mx + w - scrInset, bodyBottom + scrInset * 1.2],
  ].forEach(([sx, sy]) => {
    group.add(createCircle(sx, sy, scrR, 0xbbbbbb));
  });

  // ── Terminal bloğu ──
  group.add(createFilledRect(mx, my, w, d.terminalAreaH, 0x222222, 0.55));
  group.add(createRect(mx, my, w, d.terminalAreaH, 0x555555));

  // Terminal bağlantıları
  const termPad = w * 0.06;
  const termSlotW = (w - 2 * termPad) / d.terminalCount;
  for (let i = 0; i < d.terminalCount; i++) {
    const tx = mx + termPad + i * termSlotW;
    const slotW = termSlotW * 0.76;
    const slotH = d.terminalAreaH * 0.64;
    const slotX = tx + (termSlotW - slotW) / 2;
    const slotY = my + d.terminalAreaH * 0.18;
    group.add(createRect(slotX, slotY, slotW, slotH, 0x555555));
  }

  // ── Kanca (hook) ──
  const hookR = 0.4;
  group.add(createLine(cx - 0.3, bodyTop, cx - 0.3, my + h - hookR * 2, 0x999999));
  group.add(createLine(cx + 0.3, bodyTop, cx + 0.3, my + h - hookR * 2, 0x999999));
  group.add(createCircle(cx, my + h - hookR, hookR, 0x999999));
}

// ─── Gerçek Ölçülü Sigorta Çizimi ───────────────────────

/**
 * Gerçek fiziksel ölçülere dayalı sigorta/AOS çizimi (Three.js)
 * @param {THREE.Group} group
 * @param {number} bx - Sigorta sol-alt köşe X (cm)
 * @param {number} by - Sigorta alt kenar Y (cm, Three.js: yukarı pozitif)
 * @param {number} phase - 1 veya 3
 */
function drawRealisticBreaker(group, bx, by, phase) {
  const d = getBreakerPhysical(phase);
  const pw = d.poleWidth;
  const h = d.height;

  // Three.js Y yukarı pozitif — sigorta alt kenarı by, üst kenarı by + h
  // Dikey bölge sınırları (alttan yukarı)
  const bottomTermEnd = by + d.bottomTermH;
  const lowerBodyEnd = bottomTermEnd + d.lowerBodyH;
  const arcEnd = lowerBodyEnd + d.arcWindowH;
  const handleEnd = arcEnd + d.handleH;
  const upperBodyEnd = handleEnd + d.upperBodyH;
  // const topTermEnd = by + h; // üst kenar

  for (let i = 0; i < d.poles; i++) {
    const px = bx + i * pw;
    const pcx = px + pw / 2;

    // Kutup gövdesi
    group.add(createFilledRect(px, by, pw, h, 0xf0ece6, 0.45));
    group.add(createRect(px, by, pw, h, 0x666666));

    // Bölge ayırıcı çizgileri
    group.add(createLine(px, bottomTermEnd, px + pw, bottomTermEnd, 0xaaaaaa));
    group.add(createLine(px, lowerBodyEnd, px + pw, lowerBodyEnd, 0xaaaaaa));
    group.add(createLine(px, arcEnd, px + pw, arcEnd, 0xaaaaaa));
    group.add(createLine(px, handleEnd, px + pw, handleEnd, 0xaaaaaa));
    group.add(createLine(px, upperBodyEnd, px + pw, upperBodyEnd, 0xaaaaaa));

    // Üst terminal vidası
    const topScrewY = upperBodyEnd + d.topTermH * 0.5;
    group.add(createCircle(pcx, topScrewY, d.screwR, 0xbbbbbb));

    // Alt terminal vidası
    const bottomScrewY = by + d.bottomTermH * 0.45;
    group.add(createCircle(pcx, bottomScrewY, d.screwR, 0xbbbbbb));

    // Tutamak/kol (handle)
    const handlePadX = pw * 0.15;
    const handlePadY = d.handleH * 0.1;
    const hx = px + handlePadX;
    const hy = arcEnd + handlePadY;
    const hw = pw - 2 * handlePadX;
    const hh = d.handleH - 2 * handlePadY;
    group.add(createFilledRect(hx, hy, hw, hh, 0x5577aa, 0.6));
    group.add(createRect(hx, hy, hw, hh, 0x3a5580));

    // Ark penceresi
    const awPad = pw * 0.2;
    const awH = d.arcWindowH * 0.7;
    const awY = lowerBodyEnd + d.arcWindowH * 0.15;
    group.add(createFilledRect(px + awPad, awY, pw - 2 * awPad, awH, 0x222222, 0.6));
    group.add(createRect(px + awPad, awY, pw - 2 * awPad, awH, 0x444444));
  }

  // Çoklu kutup bağlantı çubuğu
  if (d.poles > 1) {
    const barY = arcEnd + d.handleH * 0.42;
    const barH = d.handleH * 0.16;
    const barX = bx + pw * 0.35;
    const barW = d.width - pw * 0.7;
    group.add(createFilledRect(barX, barY, barW, barH, 0x446688, 0.55));
    group.add(createRect(barX, barY, barW, barH, 0x3a5580));
  }
}

// ─── Sayaç Bölmesi Çizimi ──────────────────────────────

function drawMeterCompartment(group, originX, originY, meters, computed, config) {
  const { meterCompartmentSize, meterGrid } = computed;
  const mW = meterCompartmentSize.width;
  const mH = meterCompartmentSize.height;
  const frame = config.frame.thickness;

  // Dış çerçeve
  group.add(createRect(originX, originY, mW, mH, COLORS.frame));
  // İç çerçeve
  group.add(createRect(originX + frame, originY + frame, mW - 2 * frame, mH - 2 * frame, COLORS.innerFrame));

  // Sayaç pozisyonları
  const positions = generateMeterPositions(meters, meterGrid, config);
  positions.forEach(pos => {
    const sx = originX + pos.x;
    const sy = originY + mH - pos.y - pos.height; // Y ters (aşağıdan yukarı)

    // Hücre çerçevesi
    group.add(createRect(sx, sy, pos.width, pos.height, COLORS.innerFrame));

    // Gerçek ölçülü sayaç — hücre içinde ortalanmış
    const d = getMeterPhysical(pos.phase);
    const meterPadX = (pos.width - d.width) / 2;
    const meterShift = pos.phase === 3 ? 8 : 5; // 3F: 8cm, 1F: 5cm aşağı kaydır
    const meterPadY = 1.5 + meterShift; // hücre üstünden boşluk + kayma
    const mx = sx + meterPadX;
    const my_meter = sy + pos.height - meterPadY - d.height;
    drawRealisticMeter(group, mx, my_meter, pos.phase);

    // Gerçek ölçülü sigorta — sayacın altında
    const bd = getBreakerPhysical(pos.phase);
    const breakerGap = 1 + 2; // 1cm baz + 2cm ek boşluk (sayaç-AOS arası açık)
    const breakerTopY = my_meter - breakerGap;
    const breakerFits = (breakerTopY - bd.height) > (sy + 1);
    if (breakerFits) {
      const breakerX = sx + (pos.width - bd.width) / 2;
      const breakerY = breakerTopY - bd.height;
      drawRealisticBreaker(group, breakerX, breakerY, pos.phase);
    }

    // Sayaç etiketi (hücre üstünde, faza göre aşağı kaydırılmış)
    const labelShift = pos.phase === 3 ? 7 : 5;
    const label = pos.label || `${pos.phase}F`;
    group.add(createTextSprite(label, sx + pos.width / 2, sy + pos.height - 0.5 - labelShift, 4, COLORS.text));
  });
}

// ─── Bara Bölmesi Çizimi ────────────────────────────────

function drawBusbarCompartment(group, originX, originY, busbarRect, config) {
  const bW = busbarRect.width;
  const bH = busbarRect.height;
  const frame = config.frame.thickness;

  group.add(createRect(originX, originY, bW, bH, COLORS.frame));
  group.add(createRect(originX + frame, originY + frame, bW - 2 * frame, bH - 2 * frame, COLORS.innerFrame));

  // Bara çizgileri (R, S, T, N, PE)
  const baraLabels = ['R', 'S', 'T', 'N', 'PE'];
  const baraColors = [0x8B4513, 0x333333, 0x888888, 0x0000ff, 0x00ff00];
  const spacing = (bH - 2 * frame) / (baraLabels.length + 1);

  baraLabels.forEach((label, i) => {
    const y = originY + frame + spacing * (i + 1);
    group.add(createLine(originX + frame + 2, y, originX + bW - frame - 2, y, baraColors[i]));
    group.add(createTextSprite(label, originX + frame + 5, y + 1.5, 4, COLORS.text));
  });

  group.add(createTextSprite('BARA BÖLMESİ', originX + bW / 2, originY + bH / 2, 6, COLORS.text));
}

// ─── Dağıtım Bölmesi Çizimi ────────────────────────────

// ─── TMS (Termik Manyetik Şalter) Ön Görünüş ─────────

function getTmsAmperageScale(current) {
  if (current <= 63) return 1.0;
  if (current <= 160) return 1.25;
  if (current <= 250) return 1.5;
  return 1.75;
}

const TMS_OUTER = {
  4: [
    [-4.75,-7.5],[-3.75,-7.5],[-3.75,-6],[-2.25,-6],[-2.25,-7.5],
    [-0.75,-7.5],[-0.75,-6],[0.75,-6],[0.75,-7.5],
    [2.25,-7.5],[2.25,-6],[3.75,-6],[3.75,-7.5],[4.75,-7.5],
    [4.75,7.5],
    [3.75,7.5],[3.75,6],[2.25,6],[2.25,7.5],
    [0.75,7.5],[0.75,6],[-0.75,6],[-0.75,7.5],
    [-2.25,7.5],[-2.25,6],[-3.75,6],[-3.75,7.5],[-4.75,7.5],
  ],
  3: [
    [-3.25,-7.5],[-2.25,-7.5],[-2.25,-6],[-0.75,-6],[-0.75,-7.5],
    [0.75,-7.5],[0.75,-6],[2.25,-6],[2.25,-7.5],[3.25,-7.5],
    [3.25,7.5],
    [2.25,7.5],[2.25,6],[0.75,6],[0.75,7.5],
    [-0.75,7.5],[-0.75,6],[-2.25,6],[-2.25,7.5],[-3.25,7.5],
  ],
};
const TMS_BUS = { 4: [-5.5, -2.5, 2.5, 5.5], 3: [-5.0, 0, 5.0] };
const TMS_HALFW = { 4: 4.75, 3: 3.25 };

/**
 * TMS ön görünüş çizimi (Three.js)
 * @param {THREE.Group} group
 * @param {number} cx - Merkez X (cm)
 * @param {number} cy - Merkez Y (cm, Y yukarı pozitif)
 * @param {number} s  - Temel ölçek çarpanı
 * @param {number} poles - Kutup sayısı (3 veya 4)
 * @param {number} current - Amperaj (A)
 */
function drawTms(group, cx, cy, s, poles = 4, current = 100) {
  const ampScale = getTmsAmperageScale(current);
  const sc = s * ampScale;
  const color = 0x999999;
  const p = poles === 3 ? 3 : 4;
  const halfW = TMS_HALFW[p];

  // Dış gövde polyline
  const outerPoints = TMS_OUTER[p];
  for (let i = 0; i < outerPoints.length; i++) {
    const [x1, y1] = outerPoints[i];
    const [x2, y2] = outerPoints[(i + 1) % outerPoints.length];
    group.add(createLine(cx + x1 * sc, cy + y1 * sc, cx + x2 * sc, cy + y2 * sc, color));
  }

  // Ana gövde dikdörtgen
  group.add(createRect(cx - 1.5 * sc, cy - 2.5 * sc, 3 * sc, 5 * sc, color));

  // İç tutamak
  group.add(createFilledRect(cx - 0.75 * sc, cy - 1.5 * sc, 1.5 * sc, 3 * sc, 0x5577aa, 0.6));
  group.add(createRect(cx - 0.75 * sc, cy - 1.5 * sc, 1.5 * sc, 3 * sc, 0x3a5580));

  // Tutamak detay polyline
  const handlePoints = [
    [-0.5, 0], [-0.5, 1.2], [-0.7, 1.2], [-0.7, 1.6],
    [0.7, 1.6], [0.7, 1.2], [0.5, 1.2], [0.5, 0],
  ];
  for (let i = 0; i < handlePoints.length; i++) {
    const [x1, y1] = handlePoints[i];
    const [x2, y2] = handlePoints[(i + 1) % handlePoints.length];
    group.add(createLine(cx + x1 * sc, cy + y1 * sc, cx + x2 * sc, cy + y2 * sc, 0x3a5580));
  }
  group.add(createFilledRect(cx - 0.7 * sc, cy + 1.2 * sc, 1.4 * sc, 0.4 * sc, 0x5577aa, 0.5));

  // Yatay bara çizgileri (kutup sayısına göre)
  TMS_BUS[p].forEach(yOff => {
    group.add(createLine(cx - halfW * sc, cy + yOff * sc, cx + halfW * sc, cy + yOff * sc, color));
  });

  // Terminal vidaları
  const scrR = 0.2 * sc;
  [[-1.5, -6.5], [1.5, -6.5], [-1.5, 6.5], [1.5, 6.5]].forEach(([x, y]) => {
    group.add(createCircle(cx + x * sc, cy + y * sc, scrR, color));
    group.add(createLine(cx + x * sc - scrR * 0.7, cy + y * sc, cx + x * sc + scrR * 0.7, cy + y * sc, color));
    group.add(createLine(cx + x * sc, cy + y * sc - scrR * 0.7, cx + x * sc, cy + y * sc + scrR * 0.7, color));
  });

  // Gösterge dairesi
  group.add(createCircle(cx - 2.25 * sc, cy + 2.5 * sc, 0.3 * sc, color));

  // Amperaj etiketi
  group.add(createTextSprite(`${current}A ${p}P`, cx, cy - 8.5 * sc, Math.max(3, 1.2 * sc), '#aaaaaa'));
}

/**
 * AOS sigortalarini satirlara yerlestir (tasarsa alt satira gec)
 * @param {Array} aosBreakers - [{ phase, count }]
 * @param {number} availableW - Kullanilabilir genislik (cm)
 * @param {number} breakerGap - Sigortalar arasi bosluk (cm)
 * @returns {{ rows: Array<Array<{phase, offsetX}>>, rowCount, maxBreakerH }}
 */
function layoutAosBreakers(aosBreakers, availableW, breakerGap) {
  const items = [];
  let maxBreakerH = 0;
  aosBreakers.forEach(b => {
    const bd = getBreakerPhysical(b.phase);
    if (bd.height > maxBreakerH) maxBreakerH = bd.height;
    for (let c = 0; c < b.count; c++) {
      items.push({ phase: b.phase, width: bd.width });
    }
  });
  if (items.length === 0) return { rows: [], rowCount: 0, maxBreakerH: 0 };

  const rows = [];
  let currentRow = [];
  let cursorX = 0;
  items.forEach(item => {
    if (cursorX > 0 && cursorX + item.width > availableW) {
      rows.push(currentRow);
      currentRow = [];
      cursorX = 0;
    }
    currentRow.push({ phase: item.phase, offsetX: cursorX });
    cursorX += item.width + breakerGap;
  });
  if (currentRow.length > 0) rows.push(currentRow);
  return { rows, rowCount: rows.length, maxBreakerH };
}

function drawDistributionCompartment(group, originX, originY, distSize, config) {
  const dW = distSize.width;
  const dH = distSize.height;
  const frame = config.frame.thickness;

  group.add(createRect(originX, originY, dW, dH, COLORS.frame));
  group.add(createRect(originX + frame, originY + frame, dW - 2 * frame, dH - 2 * frame, COLORS.innerFrame));

  const innerW = dW - 2 * frame;
  const innerH = dH - 2 * frame;
  const cx = originX + frame;

  // Y yukari pozitif — originY alt kenar, originY + dH ust kenar
  // Yerlesim: ANA KESICI (ust) → bos alan (orta) → AOS (alt, yukari buyur)
  const kesiciOffsetCm = 10;
  const aosOffsetCm = 10;

  // Ana Kesici (ust — ic cerceve ustunden 10cm asagi) — TMS on gorunus
  const kesiciH = innerH * 0.15;
  const kesiciY = originY + dH - frame - kesiciH - kesiciOffsetCm;
  group.add(createFilledRect(cx + 2, kesiciY, innerW - 4, kesiciH, COLORS.breaker, 0.2));
  group.add(createRect(cx + 2, kesiciY, innerW - 4, kesiciH, COLORS.breaker));
  // TMS sembolunu Ana Kesici alanina sigdir
  const mb = config.distributionCompartment?.mainBreaker || {};
  const mbPoles = mb.poles || 4;
  const mbCurrent = mb.current || 100;
  const mbHalfW = TMS_HALFW[mbPoles === 3 ? 3 : 4];
  const tmsCx = originX + dW / 2;
  const tmsCy = kesiciY + kesiciH / 2;
  // Max amperaj ölçeğine göre fit et, düşük amperajlar orantılı küçük kalsın
  const maxAmpScale = 1.75;
  const tmsS = Math.min(
    innerW / (mbHalfW * 2 * maxAmpScale + 4),
    kesiciH / (17 * maxAmpScale)
  ) * 0.85;
  drawTms(group, tmsCx, tmsCy, tmsS, mbPoles, mbCurrent);
  group.add(createTextSprite('ANA KESİCİ', cx + 4, kesiciY + kesiciH - 1, 3, '#777777'));

  // AOS (alt — satirlara gore yukari buyur)
  const aosBreakers = config.distributionCompartment?.aos?.breakers || [];
  const aosPadding = 1.5; // cm
  const breakerGap = 1;   // cm
  const rowGap = 1;        // cm — satirlar arasi bosluk
  const availableW = innerW - 4 - 2 * aosPadding;
  const aosLayout = layoutAosBreakers(aosBreakers, availableW, breakerGap);

  let aosH = 0;
  if (aosLayout.rowCount > 0) {
    aosH = aosLayout.rowCount * aosLayout.maxBreakerH
         + (aosLayout.rowCount - 1) * rowGap
         + 2 * aosPadding;
  } else {
    aosH = innerH * 0.12;
  }
  const aosY = originY + frame + aosOffsetCm;

  group.add(createFilledRect(cx + 2, aosY, innerW - 4, aosH, COLORS.distribution, 0.2));
  group.add(createRect(cx + 2, aosY, innerW - 4, aosH, COLORS.distribution));

  if (aosBreakers.length === 0) {
    group.add(createTextSprite('AOS', originX + dW / 2, aosY + aosH / 2, 5, COLORS.text));
  } else {
    // Sigortalari satirlara gore ciz (Y yukari pozitif — ust satirdan asagiya)
    aosLayout.rows.forEach((row, rowIdx) => {
      // Ust satir en yukarda, sonraki satirlar asagida
      const rowY = aosY + aosH - aosPadding - aosLayout.maxBreakerH
                 - rowIdx * (aosLayout.maxBreakerH + rowGap);
      row.forEach(item => {
        const bx = cx + 2 + aosPadding + item.offsetX;
        drawRealisticBreaker(group, bx, rowY, item.phase);
      });
    });
    group.add(createTextSprite('AOS', cx + 4, aosY + aosH - 1, 3, '#777777'));
  }

  group.add(createTextSprite('DAĞITIM BÖLMESİ', originX + dW / 2, originY + dH / 2 + 5, 4, '#777777'));
}

// ─── Kapak Görünümü ─────────────────────────────────────
// Tüm pano çizilir, sadece sayaç bölmesi kapaklıdır. Kapaklar dikey (tam yükseklik, genişliğe göre bölünmüş).

function drawDoorOverlay(group, originX, originY, meters, computed, layout, config) {
  const { meterCompartmentSize, meterGrid, doorCount, busbarSize, distributionSize } = computed;
  const mW = meterCompartmentSize.width;
  const mH = meterCompartmentSize.height;

  // Sayaç bölmesi — kapaklı
  group.add(createRect(originX, originY, mW, mH, COLORS.frame));
  const doorRects = generateDoorRects(meterCompartmentSize, meterGrid, doorCount, config);
  doorRects.forEach(door => {
    const dx = originX + door.x;
    const dy = originY;
    group.add(createFilledRect(dx, dy, door.width, door.height, COLORS.door, 0.3));
    group.add(createRect(dx, dy, door.width, door.height, COLORS.door));
    group.add(createCircle(dx + door.width / 2, dy + door.height / 2, 1.2, COLORS.doorKnob));
    group.add(createTextSprite(door.label, dx + door.width / 2, dy + door.height / 2 + 4, 5, COLORS.text));
  });

  // Dağıtım bölmesi (sağ üst — kapaksız, sadece çerçeve)
  const dW = distributionSize.width;
  const dH = distributionSize.height;
  const distX = originX + mW;
  const distY = originY + mH - dH;
  group.add(createRect(distX, distY, dW, dH, COLORS.frame));
  group.add(createTextSprite('DAĞITIM', distX + dW / 2, distY + dH / 2, 5, '#777777'));

  // Bara bölmesi (alt — kapaksız, sadece çerçeve)
  const bW = busbarSize.width;
  const bH = busbarSize.height;
  const busbarX = originX;
  const busbarY = originY - bH;
  group.add(createRect(busbarX, busbarY, bW, bH, COLORS.frame));
  group.add(createTextSprite('BARA', busbarX + bW / 2, busbarY + bH / 2, 5, '#777777'));
}

// ─── Ölçülendirme Katmanı ───────────────────────────────

function drawDimensions(group, originX, originY, computed, config) {
  const { meterCompartmentSize, busbarSize, distributionSize, totalSize } = computed;
  const { positions, offset, lineLength } = config.dimensions;

  const mW = meterCompartmentSize.width;
  const mH = meterCompartmentSize.height;
  const bH = busbarSize.height;
  const dW = distributionSize.width;
  const tW = totalSize.width;
  const tH = totalSize.height;

  const prec = config.dimensions.precision;

  // Üst ölçüler (pano üst kenarından YUKARI doğru, dir=1)
  if (positions.top) {
    const topY = originY + mH;
    drawDimensionH(group, originX, originX + mW, topY, offset, `${mW.toFixed(prec)}`, config, 1);
    drawDimensionH(group, originX + mW, originX + mW + dW, topY, offset, `${dW.toFixed(prec)}`, config, 1);
    drawDimensionH(group, originX, originX + tW, topY, offset + lineLength, `${tW.toFixed(prec)}`, config, 1);
  }

  // Alt ölçüler (pano alt kenarından AŞAĞI doğru, dir=-1)
  if (positions.bottom) {
    const bottomY = originY - bH;
    drawDimensionH(group, originX, originX + mW, bottomY, offset, `${mW.toFixed(prec)}`, config, -1);
    drawDimensionH(group, originX + mW, originX + mW + dW, bottomY, offset, `${dW.toFixed(prec)}`, config, -1);
    drawDimensionH(group, originX, originX + tW, bottomY, offset + lineLength, `${tW.toFixed(prec)}`, config, -1);
  }

  // Sol ölçüler (pano sol kenarından SOLA doğru, dir=-1)
  if (positions.left) {
    drawDimensionV(group, originY, originY + mH, originX, offset, `${mH.toFixed(prec)}`, config, -1);
    drawDimensionV(group, originY - bH, originY, originX, offset, `${bH.toFixed(prec)}`, config, -1);
    drawDimensionV(group, originY - bH, originY + mH, originX, offset + lineLength, `${tH.toFixed(prec)}`, config, -1);
  }

  // Sağ ölçüler (pano sağ kenarından SAĞA doğru, dir=1)
  if (positions.right) {
    drawDimensionV(group, originY, originY + mH, originX + tW, offset, `${mH.toFixed(prec)}`, config, 1);
    drawDimensionV(group, originY - bH, originY, originX + tW, offset, `${bH.toFixed(prec)}`, config, 1);
    drawDimensionV(group, originY - bH, originY + mH, originX + tW, offset + lineLength, `${tH.toFixed(prec)}`, config, 1);
  }
}

// ─── Topology Çizim Fonksiyonları ───────────────────────

/**
 * Topology A — Standart (varsayılan)
 * Sol üst: Sayaç | Sağ üst: Dağıtım
 * Alt: Bara
 */
function drawTopologyA(group, originX, originY, meters, computed, layout, config) {
  const { meterCompartmentSize, busbarSize, distributionSize } = computed;
  const mH = meterCompartmentSize.height;

  // Sayaç bölmesi (sol üst)
  drawMeterCompartment(group, originX, originY, meters, computed, config);

  // Dağıtım bölmesi (sağ üst)
  const distX = originX + layout.distRect.x;
  const distY = originY + mH - distributionSize.height;
  drawDistributionCompartment(group, distX, distY, distributionSize, config);

  // Bara bölmesi (alt)
  const busbarX = originX + layout.busbarRect.x;
  const busbarY = originY - busbarSize.height;
  drawBusbarCompartment(group, busbarX, busbarY, layout.busbarRect, config);
}

/**
 * Topology B — Yan Yana (çok sayaçlı bölme)
 * Sayaç(1) | Sayaç(2) | Dağıtım
 * Bara
 */
function drawTopologyB(group, originX, originY, meters, computed, layout, config) {
  const splitInfo = calculateCompartmentSplit(meters, config);
  const compartmentCount = splitInfo.compartmentCount;

  if (compartmentCount <= 1) {
    // Bölme gerekmiyorsa Topology A çiz
    drawTopologyA(group, originX, originY, meters, computed, layout, config);
    return;
  }

  const metersPerComp = splitInfo.metersPerCompartment;
  let currentX = originX;
  const { meterCompartmentSize, busbarSize, distributionSize } = computed;

  // Her bölme için ayrı sayaç bölmesi çiz
  for (let i = 0; i < compartmentCount; i++) {
    const startIdx = i * metersPerComp;
    const endIdx = Math.min(startIdx + metersPerComp, meters.length);
    const compMeters = meters.slice(startIdx, endIdx);

    if (compMeters.length === 0) continue;

    // Her bölmenin kendi boyutunu hesapla
    const compComputed = calculateTotalPanelSize(compMeters, config);
    drawMeterCompartment(group, currentX, originY, compMeters, compComputed, config);

    currentX += compComputed.meterCompartmentSize.width;
  }

  // Dağıtım bölmesi (en sağda)
  drawDistributionCompartment(group, currentX, originY + meterCompartmentSize.height - distributionSize.height, distributionSize, config);

  // Bara bölmesi (alt, tüm genişlik)
  const totalBaraWidth = currentX - originX + distributionSize.width;
  const baraRect = { width: totalBaraWidth, height: busbarSize.height };
  drawBusbarCompartment(group, originX, originY - busbarSize.height, baraRect, config);
}

/**
 * Topology C — Dikey Bölme (ayrı pano)
 * Her bölme kendi dağıtım + barasıyla ayrı pano oluşturur
 */
function drawTopologyC(group, originX, originY, meters, computed, layout, config) {
  const splitInfo = calculateCompartmentSplit(meters, config);
  const compartmentCount = splitInfo.compartmentCount;

  if (compartmentCount <= 1) {
    drawTopologyA(group, originX, originY, meters, computed, layout, config);
    return;
  }

  const metersPerComp = splitInfo.metersPerCompartment;
  let currentX = originX;
  const gap = 10; // Panolar arası boşluk (cm)

  for (let i = 0; i < compartmentCount; i++) {
    const startIdx = i * metersPerComp;
    const endIdx = Math.min(startIdx + metersPerComp, meters.length);
    const compMeters = meters.slice(startIdx, endIdx);

    if (compMeters.length === 0) continue;

    const compComputed = calculateTotalPanelSize(compMeters, config);
    const compLayout = getCompartmentLayout(compComputed, config);

    // İlk panoda dağıtım var, diğerlerinde yok
    if (i === 0) {
      drawTopologyA(group, currentX, originY, compMeters, compComputed, compLayout, config);
      currentX += compComputed.totalSize.width + gap;
    } else {
      // Sadece sayaç + bara
      drawMeterCompartment(group, currentX, originY, compMeters, compComputed, config);
      const bH = compComputed.busbarSize.height;
      const bRect = { width: compComputed.meterCompartmentSize.width, height: bH };
      drawBusbarCompartment(group, currentX, originY - bH, bRect, config);
      currentX += compComputed.meterCompartmentSize.width + gap;
    }
  }
}

// ─── Görünüm Çerçevesi ──────────────────────────────────

/**
 * viewGroup'un BBox'ından çerçeve koordinatlarını hesapla (çizmeden)
 * @returns {{ left, bottom, right, top }} | null
 */
function computeViewFrame(viewGroup, config) {
  const margin = config.views?.frameMargin ?? 15;
  const box = new THREE.Box3().setFromObject(viewGroup);
  if (box.isEmpty()) return null;
  return {
    left:   box.min.x / Scl - margin,
    bottom: box.min.y / Scl - margin,
    right:  box.max.x / Scl + margin,
    top:    box.max.y / Scl + margin,
  };
}

/**
 * Hesaplanan çerçeve koordinatlarından dikdörtgen çiz
 */
function drawFrameRect(parentGroup, frame) {
  if (!frame) return;
  parentGroup.add(createRect(frame.left, frame.bottom,
    frame.right - frame.left, frame.top - frame.bottom, 0x444444));
}

/**
 * İki çerçeveyi hizala — yan yana ise Y, alt alta ise X eşitle
 * Asla daraltma — sadece genişletme (min left, max right, min bottom, max top)
 */
function alignFrames(intFrame, doorFrame, doorViewBelow) {
  if (!intFrame || !doorFrame) return;
  if (doorViewBelow) {
    // Alt alta → X hizala (left/right aynı)
    const alignedLeft = Math.min(intFrame.left, doorFrame.left);
    const alignedRight = Math.max(intFrame.right, doorFrame.right);
    intFrame.left = alignedLeft;   doorFrame.left = alignedLeft;
    intFrame.right = alignedRight; doorFrame.right = alignedRight;
  } else {
    // Yan yana → Y hizala (top/bottom aynı)
    const alignedTop = Math.max(intFrame.top, doorFrame.top);
    const alignedBottom = Math.min(intFrame.bottom, doorFrame.bottom);
    intFrame.top = alignedTop;       doorFrame.top = alignedTop;
    intFrame.bottom = alignedBottom; doorFrame.bottom = alignedBottom;
  }
}

// ─── Pano Bölme (Split) Yardımcıları ────────────────────

/**
 * Sayaç dizisini sütun bölme oranlarına göre gruplara ayır
 * @param {Array} meters - Tüm sayaçlar
 * @param {number[]} parts - Bölme parçaları, ör: [4, 1]
 * @param {object} config - Panel config
 * @returns {Array<Array>} Sayaç grupları
 */
export function splitMetersIntoGroups(meters, parts, config) {
  const grid = calculateMeterGrid(meters, config);
  const meters3 = meters.filter(m => m.phase === 3);
  const meters1 = meters.filter(m => m.phase === 1);
  const count3F = meters3.length;
  const count1F = meters1.length;
  const totalParts = parts.reduce((a, b) => a + b, 0);

  const groups = [];
  let taken3 = 0;
  let taken1 = 0;

  if (grid.placementMode === 'column') {
    let usedCols3 = 0;
    parts.forEach((partCols, idx) => {
      const remaining3Cols = grid.cols3 - usedCols3;
      const cols3InPart = Math.min(partCols, Math.max(remaining3Cols, 0));
      const cols1InPart = partCols - cols3InPart;

      const count3InPart = Math.min(cols3InPart * Math.max(grid.rows3, 1), count3F - taken3);
      const count1InPart = idx === parts.length - 1
        ? count1F - taken1
        : Math.min(cols1InPart * Math.max(grid.rows1, 1), count1F - taken1);

      groups.push([
        ...meters3.slice(taken3, taken3 + count3InPart),
        ...meters1.slice(taken1, taken1 + count1InPart),
      ]);
      taken3 += count3InPart;
      taken1 += count1InPart;
      usedCols3 += cols3InPart;
    });
  } else {
    // Row mode
    const rows = count3F > 0 ? grid.rows3 : Math.max(grid.rows1, 1);
    parts.forEach((partCols, idx) => {
      let count3InPart, count1InPart;
      if (count3F > 0) {
        count3InPart = Math.min(partCols * rows, count3F - taken3);
        count1InPart = idx === parts.length - 1
          ? count1F - taken1
          : Math.round(count1F * partCols / totalParts);
        count1InPart = Math.min(count1InPart, count1F - taken1);
      } else {
        count3InPart = 0;
        count1InPart = idx === parts.length - 1
          ? count1F - taken1
          : Math.min(partCols * rows, count1F - taken1);
      }

      groups.push([
        ...meters3.slice(taken3, taken3 + count3InPart),
        ...meters1.slice(taken1, taken1 + count1InPart),
      ]);
      taken3 += count3InPart;
      taken1 += count1InPart;
    });
  }

  return groups;
}

/**
 * Tek bir bölünmüş parça için ölçülendirme çiz
 */
function drawSplitPieceDimensions(group, originX, originY, width, meterH, busbarH, config, positions) {
  const pos = positions || config.dimensions.positions;
  const dimOffset = config.dimensions.offset || 5;
  const dimLineLength = config.dimensions.lineLength || 10;
  const prec = config.dimensions.precision;
  const totalH = meterH + busbarH;

  // Üst: genişlik
  if (pos.top) {
    drawDimensionH(group, originX, originX + width, originY + meterH, dimOffset, `${width.toFixed(prec)}`, config, 1);
  }
  // Alt: genişlik
  if (pos.bottom) {
    drawDimensionH(group, originX, originX + width, originY - busbarH, dimOffset, `${width.toFixed(prec)}`, config, -1);
  }
  // Sol: sayaç yüksekliği, bara yüksekliği, toplam
  if (pos.left) {
    drawDimensionV(group, originY, originY + meterH, originX, dimOffset, `${meterH.toFixed(prec)}`, config, -1);
    drawDimensionV(group, originY - busbarH, originY, originX, dimOffset, `${busbarH.toFixed(prec)}`, config, -1);
    drawDimensionV(group, originY - busbarH, originY + meterH, originX, dimOffset + dimLineLength, `${totalH.toFixed(prec)}`, config, -1);
  }
  // Sağ: sayaç yüksekliği, bara yüksekliği, toplam
  if (pos.right) {
    drawDimensionV(group, originY, originY + meterH, originX + width, dimOffset, `${meterH.toFixed(prec)}`, config, 1);
    drawDimensionV(group, originY - busbarH, originY, originX + width, dimOffset, `${busbarH.toFixed(prec)}`, config, 1);
    drawDimensionV(group, originY - busbarH, originY + meterH, originX + width, dimOffset + dimLineLength, `${totalH.toFixed(prec)}`, config, 1);
  }
}

/**
 * Bölünmüş pano çizimi
 * splitColumns ve separateDistribution kombinasyonlarını destekler
 * @returns {{ totalWidth, maxMeterH, maxBusbarH }} Çizim bilgileri
 */
function drawSplitPanel(group, originX, originY, meters, config, splitColumns, separateDistribution) {
  // Sol ölçü çizgilerinin kapladığı alanı hesapla (gap + offset + lineLength + text boşluğu)
  const dimGap = config.dimensions.gap || 0;
  const dimOff = config.dimensions.offset || 5;
  const dimLL = config.dimensions.lineLength || 10;
  const dimExtent = dimGap + dimOff + dimLL + 16; // ölçü çizgisi + yazı + ek boşluk
  const panelGap = Math.max(10, dimExtent); // cm — parçalar arası boşluk

  // Parse split: '4/1' → [4, 1]
  const parts = splitColumns && splitColumns.includes('/')
    ? splitColumns.split('/').map(Number)
    : null;

  // Referans boyutlar (tüm sayaçlardan)
  const fullComputed = calculateTotalPanelSize(meters, config);

  // ─── Sadece dağıtım ayırma (sütun bölme yok) ───
  if (!parts && separateDistribution) {
    const mW = fullComputed.meterCompartmentSize.width;
    const mH = fullComputed.meterCompartmentSize.height;
    const bH = fullComputed.busbarSize.height;
    const fullLayout = getCompartmentLayout(fullComputed, config);

    // Sayaç bölmesi
    drawMeterCompartment(group, originX, originY, meters, fullComputed, config);
    // Bara (sadece sayaç genişliğinde)
    drawBusbarCompartment(group, originX, originY - bH, { width: mW, height: bH }, config);

    // Dağıtım ayrı — gap ile sağda
    const distX = originX + mW + panelGap;
    const distH = mH + bH;
    const distW = fullComputed.distributionSize.width;
    drawDistributionCompartment(group, distX, originY - bH, { width: distW, height: distH }, config);

    // Ölçülendirme — sayaç+bara parçası
    const pos = config.dimensions.positions;
    drawSplitPieceDimensions(group, originX, originY, mW, mH, bH, config, pos);
    // Ölçülendirme — dağıtım parçası
    const prec = config.dimensions.precision;
    const dimOffset = config.dimensions.offset || 5;
    if (pos.top)    drawDimensionH(group, distX, distX + distW, originY + mH, dimOffset, `${distW.toFixed(prec)}`, config, 1);
    if (pos.bottom) drawDimensionH(group, distX, distX + distW, originY - bH, dimOffset, `${distW.toFixed(prec)}`, config, -1);
    if (pos.right)  drawDimensionV(group, originY - bH, originY + mH, distX + distW, dimOffset, `${distH.toFixed(prec)}`, config, 1);
    if (pos.left)   drawDimensionV(group, originY - bH, originY + mH, distX, dimOffset, `${distH.toFixed(prec)}`, config, -1);

    return { totalWidth: mW + panelGap + distW, maxMeterH: mH, maxBusbarH: bH };
  }

  if (!parts) return null; // fallback — normal çizime dön

  // ─── Sütun bölme aktif ───
  const meterGroups = splitMetersIntoGroups(meters, parts, config);
  let currentX = originX;
  let maxMH = 0;
  let maxBH = 0;

  meterGroups.forEach((groupMeters, idx) => {
    if (groupMeters.length === 0) return;

    const computed = calculateTotalPanelSize(groupMeters, config);
    const mW = computed.meterCompartmentSize.width;
    const mH = computed.meterCompartmentSize.height;
    const bH = computed.busbarSize.height;
    if (mH > maxMH) maxMH = mH;
    if (bH > maxBH) maxBH = bH;

    // Sayaç bölmesi
    drawMeterCompartment(group, currentX, originY, groupMeters, computed, config);

    if (!separateDistribution && idx === meterGroups.length - 1) {
      // Son grup: dağıtım yapışık
      const distX = currentX + mW;
      const distY = originY + mH - computed.distributionSize.height;
      drawDistributionCompartment(group, distX, distY, computed.distributionSize, config);

      // Bara = sayaç + dağıtım genişliği
      const bW = mW + computed.distributionSize.width;
      drawBusbarCompartment(group, currentX, originY - bH, { width: bW, height: bH }, config);
      drawSplitPieceDimensions(group, currentX, originY, bW, mH, bH, config, config.dimensions.positions);
      currentX += bW + panelGap;
    } else {
      // Sadece sayaç + bara
      drawBusbarCompartment(group, currentX, originY - bH, { width: mW, height: bH }, config);
      drawSplitPieceDimensions(group, currentX, originY, mW, mH, bH, config, config.dimensions.positions);
      currentX += mW + panelGap;
    }
  });

  // Dağıtım ayrı çizilecekse
  if (separateDistribution) {
    const distW = fullComputed.distributionSize.width;
    const distH = maxMH + maxBH;
    drawDistributionCompartment(group, currentX, originY - maxBH, { width: distW, height: distH }, config);

    // Dağıtım ölçülendirme
    const posD = config.dimensions.positions;
    const prec = config.dimensions.precision;
    const dimOffset = config.dimensions.offset || 5;
    if (posD.top)    drawDimensionH(group, currentX, currentX + distW, originY + maxMH, dimOffset, `${distW.toFixed(prec)}`, config, 1);
    if (posD.bottom) drawDimensionH(group, currentX, currentX + distW, originY - maxBH, dimOffset, `${distW.toFixed(prec)}`, config, -1);
    if (posD.right)  drawDimensionV(group, originY - maxBH, originY + maxMH, currentX + distW, dimOffset, `${distH.toFixed(prec)}`, config, 1);
    if (posD.left)   drawDimensionV(group, originY - maxBH, originY + maxMH, currentX, dimOffset, `${distH.toFixed(prec)}`, config, -1);
    currentX += distW;
  }

  return { totalWidth: currentX - originX, maxMeterH: maxMH, maxBusbarH: maxBH };
}

/**
 * Bölünmüş pano kapaklı görünüm çizimi
 */
function drawSplitDoorOverlay(group, originX, originY, meters, config, splitColumns, separateDistribution) {
  const dimGap = config.dimensions.gap || 0;
  const dimOff = config.dimensions.offset || 5;
  const dimLL = config.dimensions.lineLength || 10;
  const panelGap = Math.max(10, dimGap + dimOff + dimLL + 16);
  const parts = splitColumns && splitColumns.includes('/')
    ? splitColumns.split('/').map(Number)
    : null;
  const fullComputed = calculateTotalPanelSize(meters, config);
  const doorPos = config.dimensions.doorPositions || { top: true, bottom: false, left: false, right: true };

  const prec = config.dimensions.precision;

  // ─── Sadece dağıtım ayırma ───
  if (!parts && separateDistribution) {
    const mW = fullComputed.meterCompartmentSize.width;
    const mH = fullComputed.meterCompartmentSize.height;
    const bH = fullComputed.busbarSize.height;
    const distW = fullComputed.distributionSize.width;
    const distH = mH + bH;

    // Sayaç kapakları
    group.add(createRect(originX, originY, mW, mH, COLORS.frame));
    const doorRects = generateDoorRects(fullComputed.meterCompartmentSize, fullComputed.meterGrid, fullComputed.doorCount, config);
    doorRects.forEach(door => {
      const dx = originX + door.x;
      group.add(createFilledRect(dx, originY, door.width, door.height, COLORS.door, 0.3));
      group.add(createRect(dx, originY, door.width, door.height, COLORS.door));
      group.add(createCircle(dx + door.width / 2, originY + door.height / 2, 1.2, COLORS.doorKnob));
      group.add(createTextSprite(door.label, dx + door.width / 2, originY + door.height / 2 + 4, 5, COLORS.text));
    });
    // Kapak genişlikleri (birden fazla kapak varsa)
    if (fullComputed.doorCount > 1 && doorPos.bottom) {
      const doorW = mW / fullComputed.doorCount;
      for (let i = 0; i < fullComputed.doorCount; i++) {
        const ddx = originX + i * doorW;
        drawDimensionH(group, ddx, ddx + doorW, originY - bH, dimOff, `${doorW.toFixed(prec)}`, config, -1);
      }
    }
    // Bara çerçevesi
    group.add(createRect(originX, originY - bH, mW, bH, COLORS.frame));
    group.add(createTextSprite('BARA', originX + mW / 2, originY - bH / 2, 5, '#777777'));
    // Ölçülendirme — sayaç+bara parçası
    drawSplitPieceDimensions(group, originX, originY, mW, mH, bH, config, doorPos);
    // Dağıtım çerçevesi (ayrı)
    const distX = originX + mW + panelGap;
    group.add(createRect(distX, originY - bH, distW, distH, COLORS.frame));
    group.add(createTextSprite('DAĞITIM', distX + distW / 2, originY + mH / 2 - bH / 2, 5, '#777777'));
    // Ölçülendirme — dağıtım parçası
    if (doorPos.top)    drawDimensionH(group, distX, distX + distW, originY + mH, dimOff, `${distW.toFixed(prec)}`, config, 1);
    if (doorPos.bottom) drawDimensionH(group, distX, distX + distW, originY - bH, dimOff, `${distW.toFixed(prec)}`, config, -1);
    if (doorPos.right)  drawDimensionV(group, originY - bH, originY + mH, distX + distW, dimOff, `${distH.toFixed(prec)}`, config, 1);
    if (doorPos.left)   drawDimensionV(group, originY - bH, originY + mH, distX, dimOff, `${distH.toFixed(prec)}`, config, -1);
    return;
  }

  if (!parts) return;

  // ─── Sütun bölme ───
  const meterGroups = splitMetersIntoGroups(meters, parts, config);
  let currentX = originX;
  let maxMH = 0, maxBH = 0;

  meterGroups.forEach((groupMeters, idx) => {
    if (groupMeters.length === 0) return;

    const computed = calculateTotalPanelSize(groupMeters, config);
    const mW = computed.meterCompartmentSize.width;
    const mH = computed.meterCompartmentSize.height;
    const bH = computed.busbarSize.height;
    if (mH > maxMH) maxMH = mH;
    if (bH > maxBH) maxBH = bH;

    // Kapaklar
    group.add(createRect(currentX, originY, mW, mH, COLORS.frame));
    const doorRects = generateDoorRects(computed.meterCompartmentSize, computed.meterGrid, computed.doorCount, config);
    doorRects.forEach(door => {
      const dx = currentX + door.x;
      group.add(createFilledRect(dx, originY, door.width, door.height, COLORS.door, 0.3));
      group.add(createRect(dx, originY, door.width, door.height, COLORS.door));
      group.add(createCircle(dx + door.width / 2, originY + door.height / 2, 1.2, COLORS.doorKnob));
      group.add(createTextSprite(door.label, dx + door.width / 2, originY + door.height / 2 + 4, 5, COLORS.text));
    });

    // Kapak genişlikleri (birden fazla kapak varsa)
    if (computed.doorCount > 1 && doorPos.bottom) {
      const doorW = mW / computed.doorCount;
      for (let di = 0; di < computed.doorCount; di++) {
        const ddx = currentX + di * doorW;
        drawDimensionH(group, ddx, ddx + doorW, originY - bH, dimOff, `${doorW.toFixed(prec)}`, config, -1);
      }
    }

    if (!separateDistribution && idx === meterGroups.length - 1) {
      // Dağıtım yapışık
      const distW = computed.distributionSize.width;
      const distH = computed.distributionSize.height;
      group.add(createRect(currentX + mW, originY + mH - distH, distW, distH, COLORS.frame));
      group.add(createTextSprite('DAĞITIM', currentX + mW + distW / 2, originY + mH - distH / 2, 5, '#777777'));
      const bW = mW + distW;
      group.add(createRect(currentX, originY - bH, bW, bH, COLORS.frame));
      group.add(createTextSprite('BARA', currentX + bW / 2, originY - bH / 2, 5, '#777777'));
      // Ölçülendirme
      drawSplitPieceDimensions(group, currentX, originY, bW, mH, bH, config, doorPos);
      currentX += bW + panelGap;
    } else {
      group.add(createRect(currentX, originY - bH, mW, bH, COLORS.frame));
      group.add(createTextSprite('BARA', currentX + mW / 2, originY - bH / 2, 5, '#777777'));
      // Ölçülendirme
      drawSplitPieceDimensions(group, currentX, originY, mW, mH, bH, config, doorPos);
      currentX += mW + panelGap;
    }
  });

  // Dağıtım ayrı
  if (separateDistribution) {
    const distW = fullComputed.distributionSize.width;
    const distH = maxMH + maxBH;
    group.add(createRect(currentX, originY - maxBH, distW, distH, COLORS.frame));
    group.add(createTextSprite('DAĞITIM', currentX + distW / 2, originY + maxMH / 2 - maxBH / 2, 5, '#777777'));
    // Ölçülendirme — dağıtım parçası
    if (doorPos.top)    drawDimensionH(group, currentX, currentX + distW, originY + maxMH, dimOff, `${distW.toFixed(prec)}`, config, 1);
    if (doorPos.bottom) drawDimensionH(group, currentX, currentX + distW, originY - maxBH, dimOff, `${distW.toFixed(prec)}`, config, -1);
    if (doorPos.right)  drawDimensionV(group, originY - maxBH, originY + maxMH, currentX + distW, dimOff, `${distH.toFixed(prec)}`, config, 1);
    if (doorPos.left)   drawDimensionV(group, originY - maxBH, originY + maxMH, currentX, dimOff, `${distH.toFixed(prec)}`, config, -1);
  }
}

// ─── Ana Fonksiyon: clearPanoDetay ──────────────────────

export const clearPanoDetay = (scene) => {
  const toRemove = [];
  scene.traverse((obj) => {
    if (obj.userData?.[PANO_DETAY_TAG]) {
      toRemove.push(obj);
    }
  });
  toRemove.forEach((obj) => {
    if (obj.parent) obj.parent.remove(obj);
    else scene.remove(obj);
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      if (Array.isArray(obj.material)) {
        obj.material.forEach(m => { if (m.map) m.map.dispose(); m.dispose(); });
      } else {
        if (obj.material.map) obj.material.map.dispose();
        obj.material.dispose();
      }
    }
  });
};

// ─── Ana Fonksiyon: generatePanoDetayV2 ─────────────────

/**
 * Pano detay çizimini sahneye ekle
 * @param {THREE.Scene} scene
 * @param {Object} panoConfig
 * @param {Array}  panoConfig.meters - [{id, phase, label}]
 * @param {string} panoConfig.topology - 'A' | 'B' | 'C'
 * @param {string} panoConfig.panelName - Pano adı
 * @param {Object} panoConfig.config - PANEL_DETAIL_DEFAULTS (Redux'tan)
 * @param {Object} options - { centerX, centerY }
 */
export const generatePanoDetayV2 = (scene, panoConfig, options = {}) => {
  if (!scene) {
    console.warn('[PanoDetayV2] Scene objesi bulunamadı');
    return null;
  }

  clearPanoDetay(scene);

  const {
    meters = [],
    topology = 'A',
    panelName = 'Sayaç Panosu',
    config,
    splitColumns = '',
    separateDistribution = false,
    doorViewBelow = false,
  } = panoConfig;

  if (meters.length === 0) {
    console.warn('[PanoDetayV2] Sayaç verisi yok');
    return null;
  }

  // Boyut hesapla
  const computed = calculateTotalPanelSize(meters, config);
  const layout = getCompartmentLayout(computed, config);

  // Ana grup
  const group = new THREE.Group();
  group.userData[PANO_DETAY_TAG] = true;

  const offsetX = options.centerX ?? 0;
  const offsetY = options.centerY ?? 0;

  // Ölçü çizgilerinin üst sınırını hesapla (başlığı bunun üstüne koyacağız)
  const dimGap = config.dimensions.gap || 0;
  const dimOffset = config.dimensions.offset || 5;
  const dimLineLength = config.dimensions.lineLength || 10;
  const titleMargin = dimGap + dimOffset + dimLineLength + 12; // ölçülerin üzerinde yeterli boşluk

  // Bölme/ayırma aktif mi?
  const hasSplit = splitColumns && splitColumns.includes('/');
  const hasSeparateDist = separateDistribution;

  if (hasSplit || hasSeparateDist) {
    // ─── Bölünmüş Pano Çizimi ─────────────────
    const interiorGroup = new THREE.Group();
    const splitResult = drawSplitPanel(interiorGroup, 0, 0, meters, config, splitColumns, separateDistribution);

    if (splitResult) {
      // Başlık
      interiorGroup.add(createTextSprite(
        panelName,
        splitResult.totalWidth / 2,
        splitResult.maxMeterH + titleMargin,
        10,
        COLORS.titleText,
      ));

      group.add(interiorGroup);

      // Kapaklı görünüm
      if (config.views.withDoors) {
        const margin = config.views?.frameMargin ?? 15;
        const viewGap = config.views.viewGap || 40;

        const doorGroup = new THREE.Group();

        // Kapaklı görünümü lokal koordinatlarda (0, 0) çiz
        doorGroup.add(createTextSprite(
          'KAPAKLI GÖRÜNÜM',
          splitResult.totalWidth / 2,
          splitResult.maxMeterH + titleMargin,
          8,
          COLORS.titleText,
        ));

        drawSplitDoorOverlay(doorGroup, 0, 0, meters, config, splitColumns, separateDistribution);

        // doorGroup bbox'ını hesapla (henüz konumlanmamış)
        const doorBoxLocal = new THREE.Box3().setFromObject(doorGroup);
        const interiorBox = new THREE.Box3().setFromObject(interiorGroup);

        if (doorViewBelow) {
          const shiftY = (interiorBox.min.y / Scl - margin - viewGap) - (doorBoxLocal.max.y / Scl + margin);
          doorGroup.position.y = shiftY * Scl;
        } else {
          const shiftX = (interiorBox.max.x / Scl + margin + viewGap) - (doorBoxLocal.min.x / Scl - margin);
          doorGroup.position.x = shiftX * Scl;
        }

        group.add(doorGroup);

        // Çerçeveleri hesapla ve hizala
        const intFrame = computeViewFrame(interiorGroup, config);
        const doorFrame = computeViewFrame(doorGroup, config);
        alignFrames(intFrame, doorFrame, doorViewBelow);
        drawFrameRect(group, intFrame);
        drawFrameRect(group, doorFrame);
      } else {
        // Sadece kapaksız çerçeve
        drawFrameRect(group, computeViewFrame(interiorGroup, config));
      }
    }
  } else {
    // ─── Normal Pano Çizimi ──────────
    const interiorGroup = new THREE.Group();

    // Başlık (ölçü çizgilerinin üstünde)
    interiorGroup.add(createTextSprite(
      panelName,
      computed.totalSize.width / 2,
      computed.meterCompartmentSize.height + titleMargin,
      10,
      COLORS.titleText,
    ));

    // Topology'ye göre çiz
    switch (topology) {
      case 'B':
        drawTopologyB(interiorGroup, 0, 0, meters, computed, layout, config);
        break;
      case 'C':
        drawTopologyC(interiorGroup, 0, 0, meters, computed, layout, config);
        break;
      case 'A':
      default:
        drawTopologyA(interiorGroup, 0, 0, meters, computed, layout, config);
        break;
    }

    // Ölçülendirme (iç görünüş)
    drawDimensions(interiorGroup, 0, 0, computed, config);

    group.add(interiorGroup);

    // Kapaklı görünüm
    if (config.views.withDoors) {
      const margin = config.views?.frameMargin ?? 15;
      const viewGap = config.views.viewGap || 40;
      const doorPos = config.dimensions.doorPositions || { top: true, bottom: false, left: false, right: true };

      const mW = computed.meterCompartmentSize.width;
      const mH = computed.meterCompartmentSize.height;
      const dW = computed.distributionSize.width;
      const bH = computed.busbarSize.height;
      const tW = computed.totalSize.width;
      const tH = computed.totalSize.height;
      const prec = config.dimensions.precision;

      const doorGroup = new THREE.Group();

      // Kapaklı görünümü lokal koordinatlarda (0, 0) çiz
      // Başlık
      doorGroup.add(createTextSprite(
        'KAPAKLI GÖRÜNÜM',
        tW / 2,
        mH + titleMargin,
        8,
        COLORS.titleText,
      ));

      // Tüm pano çizilir, sadece sayaç bölmesi kapaklı
      drawDoorOverlay(doorGroup, 0, 0, meters, computed, layout, config);

      // Kapaklı görünüme ölçülendirme ekle (doorPositions'a göre)
      if (doorPos.top) {
        drawDimensionH(doorGroup, 0, mW, mH, dimOffset, `${mW.toFixed(prec)}`, config, 1);
        drawDimensionH(doorGroup, mW, mW + dW, mH, dimOffset, `${dW.toFixed(prec)}`, config, 1);
        drawDimensionH(doorGroup, 0, tW, mH, dimOffset + dimLineLength, `${tW.toFixed(prec)}`, config, 1);
      }
      if (doorPos.bottom) {
        drawDimensionH(doorGroup, 0, mW, -bH, dimOffset, `${mW.toFixed(prec)}`, config, -1);
        drawDimensionH(doorGroup, mW, mW + dW, -bH, dimOffset, `${dW.toFixed(prec)}`, config, -1);
        drawDimensionH(doorGroup, 0, tW, -bH, dimOffset + dimLineLength, `${tW.toFixed(prec)}`, config, -1);
      }
      if (doorPos.left) {
        drawDimensionV(doorGroup, 0, mH, 0, dimOffset, `${mH.toFixed(prec)}`, config, -1);
        drawDimensionV(doorGroup, -bH, 0, 0, dimOffset, `${bH.toFixed(prec)}`, config, -1);
        drawDimensionV(doorGroup, -bH, mH, 0, dimOffset + dimLineLength, `${tH.toFixed(prec)}`, config, -1);
      }
      if (doorPos.right) {
        drawDimensionV(doorGroup, 0, mH, tW, dimOffset, `${mH.toFixed(prec)}`, config, 1);
        drawDimensionV(doorGroup, -bH, 0, tW, dimOffset, `${bH.toFixed(prec)}`, config, 1);
        drawDimensionV(doorGroup, -bH, mH, tW, dimOffset + dimLineLength, `${tH.toFixed(prec)}`, config, 1);
      }

      // Kapak genişlikleri (birden fazla kapak varsa)
      if (computed.doorCount > 1 && doorPos.bottom) {
        const doorW = mW / computed.doorCount;
        for (let i = 0; i < computed.doorCount; i++) {
          const dx = i * doorW;
          drawDimensionH(doorGroup, dx, dx + doorW, -bH, dimOffset, `${doorW.toFixed(prec)}`, config, -1);
        }
      }

      // doorGroup bbox'ını hesapla ve konumla
      const doorBoxLocal = new THREE.Box3().setFromObject(doorGroup);
      const interiorBox = new THREE.Box3().setFromObject(interiorGroup);

      if (doorViewBelow) {
        const shiftY = (interiorBox.min.y / Scl - margin - viewGap) - (doorBoxLocal.max.y / Scl + margin);
        doorGroup.position.y = shiftY * Scl;
      } else {
        const shiftX = (interiorBox.max.x / Scl + margin + viewGap) - (doorBoxLocal.min.x / Scl - margin);
        doorGroup.position.x = shiftX * Scl;
      }

      group.add(doorGroup);

      // Çerçeveleri hesapla ve hizala
      const intFrame = computeViewFrame(interiorGroup, config);
      const doorFrame = computeViewFrame(doorGroup, config);
      alignFrames(intFrame, doorFrame, doorViewBelow);
      drawFrameRect(group, intFrame);
      drawFrameRect(group, doorFrame);
    } else {
      // Sadece kapaksız çerçeve
      drawFrameRect(group, computeViewFrame(interiorGroup, config));
    }
  }

  // Tüm child'lara tag ekle
  group.traverse((child) => {
    child.userData[PANO_DETAY_TAG] = true;
  });

  group.position.set(offsetX, offsetY, 0);
  scene.add(group);

  return { computed, layout };
};
